import { createContext, useContext, useReducer, useEffect } from "react";

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item, type } = action.payload;
      const existingItemIndex = state.items.findIndex(
        (cartItem) => cartItem.id === item.id && cartItem.type === type,
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += 1;
        return {
          ...state,
          items: updatedItems,
        };
      } else {
        return {
          ...state,
          items: [
            ...state.items,
            {
              ...item,
              type,
              quantity: 1,
              addedAt: Date.now(),
            },
          ],
        };
      }
    }

    case "UPDATE_QUANTITY": {
      const { id, type, quantity } = action.payload;
      const updatedItems = state.items
        .map((item) =>
          item.id === id && item.type === type
            ? { ...item, quantity: Math.max(0, quantity) }
            : item,
        )
        .filter((item) => item.quantity > 0);

      return {
        ...state,
        items: updatedItems,
      };
    }

    case "REMOVE_ITEM": {
      const { id, type } = action.payload;
      const updatedItems = state.items.filter(
        (item) => !(item.id === id && item.type === type),
      );

      return {
        ...state,
        items: updatedItems,
      };
    }

    case "CLEAR_CART":
      return {
        ...state,
        items: [],
      };

    case "LOAD_CART":
      const loadedItems = action.payload || [];

      return {
        ...state,
        items: loadedItems,
      };

    default:
      return state;
  }
};

const initialState = {
  items: [],
  isOpen: false,
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on init
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ type: "LOAD_CART", payload: parsedCart });
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state.items));
  }, [state.items]);

  const addItem = (item, type = "purchase") => {
    dispatch({
      type: "ADD_ITEM",
      payload: { item, type },
    });
  };

  const updateQuantity = (id, type, quantity) => {
    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { id, type, quantity },
    });
  };

  const removeItem = (id, type) => {
    dispatch({
      type: "REMOVE_ITEM",
      payload: { id, type },
    });
  };

  const clearCart = () => {
    dispatch({ type: "CLEAR_CART" });
  };

  const getItemCount = () => {
    return state?.items?.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotal = () => {
    return state?.items?.reduce((total, item) => {
      const price = item.price;
      return total + price * item.quantity;
    }, 0);
  };

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        getItemCount,
        getTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
